import React, { useState } from 'react';
import { FaMapMarkerAlt, FaCheck } from 'react-icons/fa';
import { LocationPicker } from './primitives';
import { geocodeLabAddress } from '../../services/diagnosticLabsService';

/* Validators shared with the address modals so an out-of-range pin blocks saving. */
export const latError = (v) =>
  v && (Number.isNaN(Number(v)) || Math.abs(Number(v)) > 90)
    ? 'Latitude must be a number between -90 and 90.'
    : '';
export const lngError = (v) =>
  v && (Number.isNaN(Number(v)) || Math.abs(Number(v)) > 180)
    ? 'Longitude must be a number between -180 and 180.'
    : '';

/**
 * Map + auto-fetch + editable latitude/longitude — the same address-section UX as the
 * diagnostic lab onboarding flow, themed for the orange booking modals. Reuses the
 * shared LocationPicker (draggable Google-Maps marker) and the backend geocoder.
 *
 * Props:
 *  - role: current admin role (for geocodeLabAddress)
 *  - address: { address_line_1, sublocality, landmark, city, state, pincode } — geocode source
 *  - latitude / longitude: current values (strings)
 *  - onChange({ latitude, longitude }): patch the parent address state
 *  - onNotify(message, type): optional toast (matches showNotification signature)
 */
const DiagnosticGeoFields = ({ role, address, latitude, longitude, onChange, onNotify }) => {
  const [geoBusy, setGeoBusy] = useState(false);

  const handleGeocode = async () => {
    setGeoBusy(true);
    try {
      const resp = await geocodeLabAddress(role, {
        address_line: address.address_line_1 || '',
        locality: address.sublocality || '',
        landmark: address.landmark || '',
        city: address.city || '',
        state: address.state || '',
        pincode: address.pincode || '',
      });
      const d = resp?.msg || resp;
      if (d?.latitude && d?.longitude) {
        onChange({ latitude: String(d.latitude), longitude: String(d.longitude) });
        onNotify?.('Coordinates fetched', 'success');
      } else {
        onNotify?.("Couldn't fetch coordinates for this address", 'error');
      }
    } catch (e) {
      onNotify?.('Geocoding failed — drag the pin or enter coordinates manually', 'error');
    } finally {
      setGeoBusy(false);
    }
  };

  const latErr = latError(latitude);
  const lngErr = lngError(longitude);
  const hasCoords = Boolean(latitude && longitude);
  const numCls = (err) =>
    `w-full bg-white border rounded-xl px-3 py-2 text-sm text-gray-800 focus:outline-none focus:ring-2 transition-all placeholder-gray-300 ${
      err ? 'border-red-300 focus:border-red-400 focus:ring-red-100' : 'border-gray-200 focus:border-orange-400 focus:ring-orange-100'
    }`;

  return (
    <div className="rounded-2xl border border-gray-200 overflow-hidden">
      {/* header + auto-fetch */}
      <div className="flex items-center justify-between gap-3 px-4 py-3 border-b border-gray-100">
        <div className="flex items-center gap-2.5 min-w-0">
          <div className="w-8 h-8 rounded-xl bg-orange-50 text-orange-500 flex items-center justify-center shrink-0">
            <FaMapMarkerAlt size={13} />
          </div>
          <div className="min-w-0">
            <p className="text-xs font-semibold uppercase tracking-wider text-gray-400">Location</p>
            <p className="text-xs text-gray-400">
              {hasCoords ? 'Drag the pin to the exact spot' : 'Auto-fetch from the address, then fine-tune'}
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={handleGeocode}
          disabled={geoBusy}
          className="shrink-0 inline-flex items-center gap-1.5 px-3 py-2 text-xs font-semibold text-orange-600 border border-orange-200 rounded-xl hover:bg-orange-50 disabled:opacity-50 transition-colors"
        >
          {geoBusy
            ? <span className="w-3.5 h-3.5 border-2 border-orange-400 border-t-transparent rounded-full animate-spin" />
            : <FaMapMarkerAlt size={12} />}
          {hasCoords ? 'Re-fetch' : 'Auto-fetch'}
        </button>
      </div>

      {/* map */}
      <div className="px-4 pt-3">
        <LocationPicker
          latitude={latitude}
          longitude={longitude}
          onChange={({ latitude: lat, longitude: lng }) => onChange({ latitude: String(lat), longitude: String(lng) })}
        />
      </div>

      {/* editable lat/long */}
      <div className="grid grid-cols-2 gap-3 px-4 py-3">
        <div>
          <label className="block text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1">Latitude</label>
          <input
            type="text"
            inputMode="decimal"
            className={numCls(latErr)}
            placeholder="e.g. 28.6999318"
            value={latitude || ''}
            onChange={e => onChange({ latitude: e.target.value.replace(/[^0-9.-]/g, '') })}
          />
          {latErr && <p className="mt-1 text-xs text-red-500">{latErr}</p>}
        </div>
        <div>
          <label className="block text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1">Longitude</label>
          <input
            type="text"
            inputMode="decimal"
            className={numCls(lngErr)}
            placeholder="e.g. 77.3329804"
            value={longitude || ''}
            onChange={e => onChange({ longitude: e.target.value.replace(/[^0-9.-]/g, '') })}
          />
          {lngErr && <p className="mt-1 text-xs text-red-500">{lngErr}</p>}
        </div>
        {hasCoords && !latErr && !lngErr && (
          <div className="col-span-2 flex items-center gap-1.5">
            <div className="w-4 h-4 rounded-full bg-green-100 flex items-center justify-center"><FaCheck size={7} className="text-green-500" /></div>
            <p className="text-xs text-green-600 font-medium">Pin set at {latitude}, {longitude}</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default DiagnosticGeoFields;
